// @ts-strict-ignore
import noop from 'lodash/noop';
import isEqual from 'lodash/isEqual';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  QueryKey,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { ColumnDef, TableState } from '@tanstack/react-table';
import type {
  Row,
  RowAction,
  ModalProps,
  Option,
} from '@usealloy/component-library';
import {
  Box,
  Button,
  ButtonVariant,
  Checkbox,
  Icon,
  IconName,
  MultiSelect,
  Token,
  Tooltip,
  makeInitialTableState,
  useSnackBar,
  useTableLocalStorage,
} from '@usealloy/component-library';

import { ajaxFetch, buildURLParamsString } from '~/helpers/api';

const ROW_ACTION_COLUMN_PROPS = ['actions', 'delete_link'] as const;
const ROW_ACTION_ICON_NAMES = {
  resend_invitation: IconName.Email,
} as const;

const FONT_AWESOME_TO_CL_ICON_MAPPING = {
  'fa-building': IconName.Business,
  'fa-clock': IconName.Clock,
  'fa-code-merge': IconName.Reconciliation,
  'fa-dollar-sign': IconName.MoneyBracket,
  'fa-envelope-open': IconName.Email,
  'fa-folder': IconName.Portfolio,
  'fa-lock': IconName.Lock,
  'fa-long-arrow-alt-right': IconName.DownRightArrow,
  'fa-mobile': IconName.Mobile,
  'fa-repeat-alt': IconName.Rerun,
  'fa-sitemap': IconName.DownRightArrow,
  'fa-user': IconName.Person,
  'fa-users': IconName.Group,
} as const;

const getSnackBarType = result => {
  switch (result) {
    case 'success':
      return 'success';
    case 'error':
      return 'danger';
    default:
      return 'info';
  }
};

interface LegacyTableParams {
  columns: Array<string>;
  orderBy: Array<[string, string]>;
  filterBy: {};
  searchBy: {};
  paginateBy: number;
  page: number;
  json?: boolean;
}

interface LegacyTableState {
  columns: Array<{
    name: string;
    prop: string;
    orderBy?: string | boolean;
  }>;
  data: { [key: number]: any };
  dataCount: number;
}

export type LegacyTableFilterBy = {
  [key: string]: [[number, string], string] | {
    should: string[];
  } | {
    filter: string[];
  } | string;
};

export type UseLegacyTableDataProps<TData> = Omit<UseQueryOptions<UseLegacyTableDataResult<TData>>, 'queryFn' | 'initialData'> & {
  url: string;
  filterBy?: LegacyTableFilterBy;
  searchBy?: { [key: string]: string };
  initialTableState?: TableState;
  transform?: (result: UseLegacyTableDataResult<TData>) => UseLegacyTableDataResult<TData>;
  renderers?: {
    [key: string]: (columnDef: ColumnDef<TData, unknown>) => ColumnDef<TData, unknown>;
  };
  clickEvents?: { [key: string]: (row: TData) => void };
  clickActions?: { [key: string]: (row: TData) => void | null };
  extraColumns?: string[];
  enableSingleCheckboxActive?: boolean;
  enableLocalStorage?: boolean;
  enableJson?: boolean;
};

interface UseLegacyTableDataResult<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: Array<TData>;
  recordCount: number;
  state: TableState;
  onTableStateChange: React.Dispatch<React.SetStateAction<TableState>>;
  modalProps: { [key in typeof ROW_ACTION_COLUMN_PROPS[number]]: ModalProps };
  rowActions: RowAction<TData>[];
  raw: { [key: string]: any };
}

interface ConvertTableStateToLegacyParamsProps {
  tableState: TableState;
  filterBy?: LegacyTableFilterBy;
  searchBy?: { [key: string]: string };
  columnData?: { [key: string]: string };
  enableJson?: boolean;
}

export const convertTableStateToLegacyParams = ({
  tableState: {
    pagination: {
      pageSize: paginateBy,
      pageIndex: page,
    },
    sorting,
  },
  filterBy,
  searchBy,
  columnData,
  enableJson,
}: ConvertTableStateToLegacyParamsProps): LegacyTableParams => ({
  columns: [],
  orderBy: sorting.map(({ id, desc }) => [columnData[id], desc ? 'DESC' : 'ASC']),
  filterBy: filterBy || {},
  searchBy: searchBy || {},
  paginateBy,
  page,
  ...(enableJson !== undefined ? {
    json: enableJson,
  } : {}),
});

interface ConvertLegacyTableStateToTableDataProps<TData> {
  tableState: TableState;
  legacyTableState: LegacyTableState;
  onTableStateChange: React.Dispatch<React.SetStateAction<TableState>>;
  modalProps: { [key in typeof ROW_ACTION_COLUMN_PROPS[number]]: ModalProps };
  raw: { [key: string]: any };
  transform?: (result: UseLegacyTableDataResult<TData>) => UseLegacyTableDataResult<TData>;
  renderers?: {
    [key: string]: (columnDef: ColumnDef<TData, unknown>) => ColumnDef<TData, unknown>;
  };
  handleCellAction: (
    event: React.ChangeEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
    row: Row<TData>,
  ) => void;
  clickActions: UseLegacyTableDataProps<TData>['clickActions'];
  enableSingleCheckboxActive: boolean;
}

function convertLegacyTableStateToTableData<TData>({
  tableState,
  legacyTableState: {
    columns,
    data,
    dataCount: recordCount,
  },
  onTableStateChange,
  modalProps,
  raw,
  transform,
  renderers,
  handleCellAction,
  clickActions,
  enableSingleCheckboxActive,
}: ConvertLegacyTableStateToTableDataProps<TData>): UseLegacyTableDataResult<TData> & Pick<UseLegacyTableDataProps<TData>, 'transform'> {
  return transform({
    columns: convertLegacyColumnsToTableColumns({
      columns,
      renderers,
      handleCellAction,
      enableSingleCheckboxActive,
    }),
    data: data[tableState.pagination.pageIndex].data,
    recordCount,
    state: {
      ...tableState,
      expanded: {},
    },
    onTableStateChange,
    rowActions: convertLegacyColumnsToRowActions({ columns, handleCellAction, clickActions }),
    modalProps,
    raw,
  });
}

interface ConvertLegacyColumnsToTableColumnsProps<TData> {
  columns: LegacyTableState['columns'];
  renderers: { [key: string]: (columnDef: ColumnDef<TData, unknown>) => ColumnDef<TData, unknown> };
  handleCellAction: (
    event: React.ChangeEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
    row: Row<TData>
  ) => void;
  enableSingleCheckboxActive: boolean;
}

function convertLegacyColumnsToTableColumns<TData>({
  columns,
  renderers,
  handleCellAction,
  enableSingleCheckboxActive,
}: ConvertLegacyColumnsToTableColumnsProps<TData>): ColumnDef<TData, unknown>[] {
  return columns.filter(
    ({ prop }) => !ROW_ACTION_COLUMN_PROPS.includes(prop),
  ).map(({ name, prop, orderBy }) => {
    const columnDef = {
      id: prop,
      header: name,
      accessorKey: prop,
      enableSorting: orderBy !== false,
      enableHiding: Boolean(name),
      cell: ({ row }) => {
        const cell = row.original[prop];

        // Cell should contain regular text
        if (['string', 'number'].indexOf(typeof cell) > -1) {
          return cell === 'None'.toLowerCase()
            ? <span className="light-grey"><i>None</i></span> : cell;
        }

        return null;
      },
    };

    if (prop.endsWith('_token')) {
      return {
        ...columnDef,
        maxSize: 132,
        cell: ({ row }) => {
          const cell = row.original[prop];

          return (
            <Token displayContent={cell} style={{ maxWidth: 100 }} />
          );
        },
      };
    }

    if (prop.endsWith('_slug')) {
      // TODO
    }

    if (prop.endsWith('_button')) {
      return {
        ...columnDef,
        maxSize: 48,
        cell: ({ row }) => {
          const cell = row.original[prop];

          if (typeof cell === 'object') {
            return (
              <Button
                className={cell.className}
                disabled={cell.disabled}
                onClick={event => handleCellAction(event, row)}
                variant={ButtonVariant.primary}
                data-name={prop}
              >
                {cell?.text}
              </Button>
            );
          }

          if (typeof cell === 'string') {
            return cell;
          }

          return false;
        },
      };
    }

    if (prop.endsWith('_link')) {
      return {
        ...columnDef,
        maxSize: 72,
        cell: ({ row }) => {
          const cell = row.original[prop];

          return (
            <a
              className="secondary"
              href={cell?.href ?? ''}
              data-name={prop}
              onClick={event => handleCellAction(event, row)}
            >
              {cell?.text}
              {/* TODO HANDLE ICONS */}
            </a>
          );
        },
      };
    }

    if (prop.endsWith('_checkbox')) {
      return {
        ...columnDef,
        size: 128,
        cell: ({ row }) => {
          const cell = row.original[prop];
          const disabled = cell.disabled || (enableSingleCheckboxActive && cell.checked);

          return (
            <Checkbox
              data-name={prop}
              value={cell.value}
              disabled={disabled}
              onChange={event => handleCellAction(event, row)}
              checked={cell.checked}
            />
          );
        },
      };
    }

    if (prop.endsWith('_select')) {
      return {
        ...columnDef,
        meta: {
          cellContentStyles: {
            width: '100%',
          },
        },
        cell: ({ row }) => {
          const cell = row.original[prop];
          const options = cell.options.map(option => ({
            label: option.text,
            value: option.value,
          }));
          const selectedOption = options.find(option => option.value === cell.value);

          return (
            <MultiSelect
              isMulti={false}
              options={options}
              selectedOptions={selectedOption}
              onChange={(option: Option<any>) => {
                const event = new Event('change', { bubbles: true }) as unknown as React.ChangeEvent<HTMLElement>;

                Object.defineProperty(event, 'currentTarget', {
                  writable: false,
                  value: {
                    value: option.value,
                    dataset: {
                      name: prop,
                    },
                  },
                });

                handleCellAction(event, row);
              }}
            />
          );
        },
      };
    }

    if (prop.endsWith('_fa')) {
      const leftAlign = prop === 'created_fa' || prop === 'updated_fa';

      return {
        ...columnDef,
        maxSize: name ? 96 : 48,
        meta: {
          disableFlex: true,
        },
        cell: ({ row }) => {
          const renderCell = cell => {
            if (!cell?.className) {
              return null;
            }

            const icon = Object.entries(FONT_AWESOME_TO_CL_ICON_MAPPING).reduce((acc, [fa, cl]) => {
              if (new RegExp(`\\b${fa}\\b`).test(cell.className)) {
                return <Icon icon={cl} />;
              }

              return acc;
            }, (
              <i
                data-name={name}
                className={cell.className}
                style={cell.style ? cell.style : {}}
              />
            ));

            return (
              <Box gap={2} {...(leftAlign && { justifyContent: 'flex-start' })}>
                {cell.prefix && (
                  <span data-name={name} className="prefix">
                    {cell.prefix}
                  </span>
                )}
                <Tooltip
                  delayDuration={0}
                  content={cell.tooltip}
                  side={cell.tooltip_placement || 'bottom'}
                  hide={!cell.tooltip}
                >
                  {icon}
                </Tooltip>
                {cell.suffix && (
                  <span data-name={name} className="suffix">
                    {cell.suffix}
                  </span>
                )}
              </Box>
            );
          };

          const cell = row.original[prop];

          if (Array.isArray(cell)) {
            return (
              <Box>
                {cell.map(item => renderCell(item))}
              </Box>
            );
          }

          return renderCell(cell);
        },
      };
    }

    return columnDef;
  }).map(columnDef => {
    if (renderers[columnDef.id]) {
      return renderers[columnDef.id](columnDef);
    }

    return columnDef;
  });
}

export function convertLegacyColumnsToRowActions<TData>({
  columns,
  handleCellAction,
  clickActions,
}: ConvertLegacyColumnsToRowActionsProps<TData>): RowAction<TData>[] {
  return columns.filter(({ prop }) => ROW_ACTION_COLUMN_PROPS.includes(prop)).map(({ prop }) => {
    switch (prop) {
      case 'delete_link':
        return {
          name: 'Delete',
          iconName: IconName.Trash,
          renderTooltip: (data => data.delete_link?.tooltip) as (
            data: TData & { delete_link?: { tooltip: string } }
          ) => string | undefined,
          onClick: row => {
            const event = new Event('change', { bubbles: true }) as unknown as React.ChangeEvent<HTMLElement>;

            Object.defineProperty(event, 'currentTarget', {
              writable: false,
              value: {
                value: '',
                dataset: {
                  name: prop,
                },
              },
            });

            handleCellAction(event, { original: row } as Row<TData>);
          },
        };
      case 'actions':
      default:
        return {
          name: '',
          onClick: noop,
          render: (({ actions }) => actions.map((action, index) => ({
            name: action.text,
            tooltip: actions.length === 1 ? action.text : undefined,
            iconName: actions.length === 1 ? ROW_ACTION_ICON_NAMES[action.property] : undefined,
            onClick: row => {
              const clickAction = clickActions[action.text];

              if (clickAction) {
                clickAction(action);

                return;
              }

              const event = new Event('change', { bubbles: true }) as unknown as React.ChangeEvent<HTMLElement>;

              Object.defineProperty(event, 'currentTarget', {
                writable: false,
                value: {
                  value: '',
                  dataset: {
                    name: 'actions',
                    index,
                  },
                },
              });

              handleCellAction(event, { original: row } as Row<TData>);
            },
          }))) as (
            data: TData & { actions: any[] }
          ) => RowAction<TData>[],
        };
    }
  });
}

interface ConvertLegacyColumnsToRowActionsProps<TData> {
  columns: LegacyTableState['columns'];
  handleCellAction: (
    event: React.ChangeEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
    row: Row<TData>
  ) => void;
  clickActions: UseLegacyTableDataProps<TData>['clickActions'];
}

/**
 * This hook is intended to be used with Daffy endpoints that use ReactTableView. Given a URL
 * (at minimum), this hook will transform ReactTableView responses to a shape expected by the
 * Component Library <Table /> component.
 */
export function useLegacyTableData<TData>(queryKey: QueryKey, {
  url,
  filterBy,
  searchBy,
  initialTableState = makeInitialTableState(),
  transform = data => data,
  renderers = {},
  clickEvents = {},
  clickActions = {},
  extraColumns = [],
  enableSingleCheckboxActive = false,
  enableLocalStorage = false,
  enableJson,
  ...useQueryProps
}: UseLegacyTableDataProps<TData>) {
  const localStorageKey = JSON.stringify(queryKey)
    .toLowerCase()
    .replace(/[^\w]/g, ' ')
    .trim()
    .replace(/ +/g, '-');
  const { getStateFromLocalStorage, saveStateToLocalStorage } = useTableLocalStorage(
    localStorageKey,
  );

  const initialColumnData = extraColumns.reduce((acc, column) => ({
    ...acc,
    [column]: column,
  }), {});
  const [columnData, setColumnData] = useState<{ [key: string]: string }>(initialColumnData);
  const [tableState, setTableState] = useState<TableState>({
    ...initialTableState,
    ...(enableLocalStorage ? getStateFromLocalStorage() : {}),
  });
  const params = convertTableStateToLegacyParams({
    tableState,
    filterBy,
    searchBy,
    enableJson,
    columnData,
  });
  const [modalProps, setModalProps] = useState<{
    [key in typeof ROW_ACTION_COLUMN_PROPS[number]]: ModalProps
  }>({
    actions: {
      isOpen: false,
      size: 'small',
    },
    delete_link: {
      isOpen: false,
      size: 'small',
    },
  });

  const addSnackBar = useSnackBar();
  const queryClient = useQueryClient();
  const queryKeyCompositePrevRef = useRef<QueryKey>([
    queryKey, params, modalProps,
  ]);
  const [queryKeyComposite, queryKeyCompositePrev] = useMemo(() => {
    const prev = queryKeyCompositePrevRef.current;
    const next = [
      queryKey, params, modalProps,
    ];

    queryKeyCompositePrevRef.current = next;

    return [next, prev];
  }, [queryKey, params, modalProps]);

  const queryKeyCompositeRef = useRef(queryKeyComposite);
  queryKeyCompositeRef.current = queryKeyComposite;

  const onTableStateChange = useCallback((newState: TableState) => {
    const currentData = queryClient.getQueryData<UseLegacyTableDataResult<TData>>(
      queryKeyCompositeRef.current,
    );
    if (currentData && isEqual(currentData.state, newState)) {
      return;
    }

    setTableState(newState);
    if (enableLocalStorage) {
      saveStateToLocalStorage(newState);
    }

    queryClient.setQueryData(
      queryKeyCompositeRef.current,
      ({ state: _state, ...oldTableData }) => ({
        ...oldTableData,
        state: newState,
      }));
  }, [queryClient, enableLocalStorage, saveStateToLocalStorage]);

  const performClickAction = useCallback((
    _event: React.ChangeEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
    name: string,
    row: Row<TData>,
  ) => {
    clickEvents[name](row.original);

    // TODO for non functions
  }, [clickEvents]);

  const performCellAction = useCallback((
    name: string,
    index: string,
    value: string,
    row: TData,
  ) => {
    const cell = (index !== undefined) ? row[name][index] : row[name];

    if (cell.method && cell.method !== 'GET') {
      const body = {};
      body[cell.property] = value;
      if (cell.extra && cell.extra.length) cell.extra.forEach(e => { body[e] = row[e]; });

      ajaxFetch(cell.href, {
        method: cell.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(data => {
        if (data.redirect_url) {
          window.location.href = data.redirect_url;
        }

        if (data.message) {
          addSnackBar({ title: data.message, type: getSnackBarType(data.result) });
        }

        if (data.result === 'success') {
          queryClient.invalidateQueries(queryKeyComposite.slice(0, 1));
        }
      });
    } else {
      window.location = cell.href;
    }
  }, [queryClient, queryKeyComposite]);

  const handleCellAction = useCallback((
    event: React.ChangeEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
    row: Row<TData>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const { currentTarget: target } = event;
    const { name, index } = target.dataset;

    if (clickEvents[name]) {
      performClickAction(event, name, row);
    } else {
      const cell = (index !== undefined) ? row.original[name][index] : row.original[name];
      const value = (target as HTMLInputElement).value || cell.value;

      if (cell.confirm) {
        const onClose = () => setModalProps(props => ({
          ...props,
          [name]: {
            ...props[name],
            isOpen: false,
          },
        }));
        const onSubmit = () => {
          performCellAction(name, index, value, row.original);
          onClose();
        };

        setModalProps(props => ({
          ...props,
          [name]: {
            ...props[name],
            isOpen: true,
            header: cell.confirm,
            onClose,
            primaryAction: (
              <Button
                type="submit"
                variant={ButtonVariant.primary}
                onClick={onSubmit}
              >
                Submit
              </Button>
            ),
            secondaryAction: (
              <Button onClick={onClose}>
                Cancel
              </Button>
            ),
          },
        }));
      } else {
        performCellAction(name, index, value, row.original);
      }
    }
  }, [clickEvents, performClickAction, performCellAction, setModalProps]);

  return useQuery<UseLegacyTableDataResult<TData>>(queryKeyComposite, {
    queryFn: async () => {
      const paramsStr = buildURLParamsString(params, true);
      const {
        tableState: legacyTableState,
        columns: legacyTableStateColumns,
        data: legacyTableStateData,
        dataCount: legacyTableStateDataCount,
        ...raw
      }: {
        tableState: LegacyTableState;
        // Sometimes the shape is different and the following fields are at the top level
        columns?: LegacyTableState['columns'];
        data?: LegacyTableState['data'];
        dataCount?: LegacyTableState['dataCount'];
      } = await ajaxFetch(`${url}${paramsStr}`);

      const tableData = convertLegacyTableStateToTableData<TData>({
        tableState,
        legacyTableState: legacyTableState ?? {
          columns: legacyTableStateColumns,
          data: legacyTableStateData,
          dataCount: legacyTableStateDataCount,
        },
        onTableStateChange,
        modalProps,
        raw,
        transform,
        renderers,
        handleCellAction,
        clickActions,
        enableSingleCheckboxActive,
      });

      setColumnData(Object.fromEntries(
        (legacyTableState?.columns ?? legacyTableStateColumns).map(({
          prop,
          orderBy,
        }) => [prop, typeof orderBy === 'string' ? orderBy : prop]),
      ));

      return tableData;
    },
    initialData: () => (queryClient.getQueryData(queryKeyCompositePrev) ?? {
      columns: [],
      data: [],
      recordCount: 0,
      state: tableState,
      onTableStateChange,
      modalProps,
      rowActions: [],
      raw: {},
    }),
    ...useQueryProps,
  });
}
